import errorMessages from './error-messages.js';
export { errorMessages };
/**
 * Never thrown by the library, but may be returned from validation functions.
 * By default the error `message` is "Validation failed ([code])"; use
 * [[`getErrorMessage`]] to get a proper localised error message,
 * or wrap the validator with [[`withLocalizedErrorMessages`]].
 *
 * When building conditional logic based on the error type, use the `code`
 * rather than the `message` value, as that stays constant. In particular,
 * the `'empty'` and `'too-short'` errors will often be encountered when
 * processing incomplete user input.
 */
export class ValidateError extends Error {
    /** @ignore */
    constructor(code) {
        super(`Validation failed (${code})`);
        /**
         * Identifier for the error type
         * @type {string}
         */
        this.code = code;
    }
}
/**
 * Get the error message corresponding to an error code.
 * Returns `undefined` if not found.
 *
 * @param language - Language identifier
 * @param code - The error code
 * @param partial - If true, the error message is expected to be shown with a application-provided prefix.
 */
export function getErrorMessage(language, code, partial) {
    if (code instanceof ValidateError) {
        code = code.code;
    }
    const l = (partial ? `${language}-part` : language);
    const data = errorMessages[code];
    return data && data[l];
}
/**
 * Add localised error messages to the output of a validator function.
 *
 * If the validator returns an error, this wrapper modifies the error `message`
 * according to the locale `lc` and sets its `partialMessage` so that it may be
 * used as `${varName} ${err.partialMessage}` to customise the subject.
 */
export function withLocalizedErrorMessages(language, validator) {
    return function withLocalizedErrorMessagesInner(...args) {
        const res = validator(...args);
        if (res instanceof ValidateError) {
            res.message = getErrorMessage(language, res.code, false);
            res.partialMessage = getErrorMessage(language, res.code, true);
        }
        return res;
    };
}
